import { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  icon: string;
  color: string;
  decimals?: number;
  duration: number;
}

const STATS: StatItem[] = [
  { value: 5, suffix: 'M+', label: 'Presentations Made', icon: 'ri-slideshow-line', color: '#f43f5e', duration: 2000 },
  { value: 300, suffix: 'K+', label: 'Active Creators', icon: 'ri-user-star-line', color: '#8b5cf6', duration: 1800 },
  { value: 4.9, suffix: '/5', label: 'Avg. Creator Rating', icon: 'ri-star-fill', color: '#f59e0b', duration: 1600, decimals: 1 },
  { value: 150, suffix: '+', label: 'Countries Reached', icon: 'ri-global-line', color: '#10b981', duration: 1700 },
];

function useCountUp(target: number, duration: number, active: boolean, decimals = 0) {
  const [count, setCount] = useState(0);
  const raf = useRef<number>(0);

  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * target;
      setCount(parseFloat(current.toFixed(decimals)));
      if (progress < 1) { raf.current = requestAnimationFrame(animate); }
      else { setCount(target); }
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [active, target, duration, decimals]);

  return count;
}

function AnimatedStat({ stat, active }: { stat: StatItem; active: boolean }) {
  const count = useCountUp(stat.value, stat.duration, active, stat.decimals ?? 0);
  const display = stat.decimals ? count.toFixed(stat.decimals) : Math.floor(count).toLocaleString();

  return (
    <div className="flex items-center gap-4 px-8 py-5 flex-1 min-w-[180px]">
      <div
        className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: stat.color + '15' }}
      >
        <i className={`${stat.icon} text-xl`} style={{ color: stat.color }}></i>
      </div>
      <div>
        <div className="flex items-baseline gap-0.5">
          <span className="text-2xl font-extrabold text-slate-900 tabular-nums leading-none">
            {display}
          </span>
          <span className="text-lg font-extrabold leading-none" style={{ color: stat.color }}>
            {stat.suffix}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1 font-medium whitespace-nowrap">{stat.label}</p>
      </div>
    </div>
  );
}

export default function PresentationStatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setActive(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center flex-wrap divide-x divide-slate-100">
          {STATS.map(stat => (
            <AnimatedStat key={stat.label} stat={stat} active={active} />
          ))}
          <div className="flex items-center gap-3 px-8 py-5 flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
              <span className="text-xs font-bold text-rose-600 whitespace-nowrap">Live Now</span>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-700 whitespace-nowrap">4,812 creating</p>
              <p className="text-xs text-slate-400">presentations right now</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
